import { useReducer, Reducer } from 'react';

interface Checkout {
  formState: 'loading' | 'success' | 'error' | 'initial';
  message?: string;
  open: boolean;
  subscriptionStatus: 'initial' | 'new' | 'updated' | 'display' | 'cancelled';
}

type LoadingEvent = {
  type: 'loading';
};

type LoadingDoneEvent = {
  type: 'loadingDone';
};

type SuccessEvent = {
  type: 'success';
  subscriptionStatus: 'initial' | 'new' | 'updated' | 'display' | 'cancelled';
};

type ErrorEvent = {
  errorMessage: string;
  type: 'error';
};

type ResetEvent = {
  type: 'reset';
};

type CheckoutChangeEvent =
  | LoadingEvent
  | LoadingDoneEvent
  | SuccessEvent
  | ErrorEvent
  | ResetEvent;

export const useCheckout = () => {
  const [{ formState, message, open, subscriptionStatus }, dispatch] =
    useReducer<Reducer<Checkout, CheckoutChangeEvent>>(
      (state, action) => {
        switch (action.type) {
          case 'loading':
            return {
              ...state,
              formState: 'loading'
            };
          case 'loadingDone':
            return {
              ...state,
              formState: 'initial'
            };
          case 'success':
            return {
              formState: 'success',
              message: undefined,
              open: action.subscriptionStatus !== 'initial',
              subscriptionStatus: action.subscriptionStatus
            };
          case 'error':
            return {
              ...state,
              formState: 'error',
              open: false,
              message: action.errorMessage
            };
          case 'reset':
            return {
              formState: 'initial',
              open: false,
              subscriptionStatus: 'initial'
            };
          default:
            return {
              formState: 'initial',
              open: false,
              subscriptionStatus: 'initial'
            };
        }
      },
      {
        formState: 'initial',
        open: false,
        subscriptionStatus: 'initial'
      }
    );

  const isLoading = formState === 'loading';
  const isError = formState === 'error';
  const isSuccess = formState === 'success' && subscriptionStatus !== 'display';

  const toLoading = () => dispatch({ type: 'loading' });
  const toLoadingDone = () => dispatch({ type: 'loadingDone' });
  const toSuccess = (
    newSubscription: 'initial' | 'new' | 'updated' | 'display' | 'cancelled'
  ) =>
    dispatch({
      subscriptionStatus: newSubscription,
      type: 'success'
    });
  const toError = (errorMessage: string) =>
    dispatch({ type: 'error', errorMessage });
  const toReset = () => dispatch({ type: 'reset' });
  return {
    isError,
    isLoading,
    isSuccess,
    message,
    open,
    subscriptionStatus,
    toError,
    toLoading,
    toLoadingDone,
    toReset,
    toSuccess
  };
};
